import { useTranslation } from 'next-i18next'
import { MutableRefObject, RefObject, useEffect, useState } from 'react'

import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { renderProductOption } from '@src/quince-components/src/atoms/productForm/ProductForm'
import SiblingProductOptions, { SiblingProductOptionType } from '@src/quince-components/src/atoms/siblingProductOptions'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant/BaseProductVariant'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { MiniPDPCollapsibleOptions } from './components/MiniPDPCollapsibleOptions'
import { useSlideDownAnimation } from './hooks/useSlideDownAnimation'
import {
  buildMiniPdpSizeSectionSummarySegments,
  MINI_PDP_MERGED_ADDON_OPTION_NAME,
} from './miniPDPProductOptions.helpers'
import styles from './miniPDPProductOptions.module.scss'

// This is one less than actual limit to account for the "+X".
const MOBILE_SWATCH_LINE_LIMIT = 8
const DESKTOP_SWATCH_LINE_LIMIT = 11

interface MiniPDPProductOptionsProps {
  product: Product
  productOptions: ProductOption[]
  selectedOptions: ProductVariantOption[]
  aboveFoldTrackingRefs?: {
    colorVariantRef: RefObject<HTMLFieldSetElement>
    sizeVariantRef: RefObject<HTMLFieldSetElement>
    productInfoRef: RefObject<HTMLDivElement>
    addToCartTrackingRef: MutableRefObject<HTMLElement | null>
  }
  onOptionSelect: (optionName: string, selectedValue: string) => void
  onSiblingOptionSelect?: (val: string, productId?: number) => void
  selectedSiblingProductId?: number | string
  productPurchaseInfo?: IProductPurchaseInfo
  inputOptionError?: string
  sizeOptionRef?: RefObject<HTMLFieldSetElement>
  isExpandedSize: boolean
  expandSizeMiniPDP: () => void
}

const MiniPDPProductOptions = (props: MiniPDPProductOptionsProps) => {
  const { t } = useTranslation('common')
  const [isMiniPdpColorRowExpanded, setIsMiniPdpColorRowExpanded] = useState(false)
  const colorRevealRef = useSlideDownAnimation(isMiniPdpColorRowExpanded)

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const swatchLineLimit = isMobile ? MOBILE_SWATCH_LINE_LIMIT : DESKTOP_SWATCH_LINE_LIMIT

  useEffect(() => {
    setIsMiniPdpColorRowExpanded(false)
  }, [props.product.id])

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const productOptions = props.productOptions
    .filter(option => !option.isHidden)
    .map(productOption => {
      return {
        node: renderProductOption({
          productOption: productOption,
          selectedOptions: props.selectedOptions,
          onOptionSelect: props.onOptionSelect,
          sizeChart: undefined,
          mergedSizeChart: undefined,
          handleSizeChart: EMPTY_ARROW_FUNC,
          hideSizeChart: true,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          renderSwatchSelectionButton: () => null,
          shouldReduceMarginBottom: false,
          showInlineSizeChart: false,
          showTruefitWidget: false,
          product: props.product,
          t: t,
          productPurchaseInfo: props.productPurchaseInfo,
          inputOptionError: props.inputOptionError,
          colorVariantRef: props.aboveFoldTrackingRefs?.colorVariantRef,
          sizeVariantRef: props.aboveFoldTrackingRefs?.sizeVariantRef,
          sizeOptionRef: props.sizeOptionRef,
          ...(productOption.name === ProductOptionName.Color
            ? {
                isMiniPDP: true,
                showMoreOption: !isMiniPdpColorRowExpanded,
                optionLimit: swatchLineLimit,
                onShowMoreClick: () => {
                  setIsMiniPdpColorRowExpanded(true)
                },
                excessOptionsTextClassName: styles.colorShowMoreTrigger,
              }
            : {}),
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
        optionName: undefined,
      }
    })

  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props?.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          className={styles.addOnOptionsContainer}
          labelClassName={styles.addOnLabel}
          optionSelectorClassName={styles.addOnOption}
          isMiniPDP={true}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
      optionName: MINI_PDP_MERGED_ADDON_OPTION_NAME,
    }
  })

  const combinedProductOptions = [...(addOnOptions ?? []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const sizeSectionEntries = combinedProductOptions.filter(
    option => option.key === ProductOptionName.Size || option.optionName === MINI_PDP_MERGED_ADDON_OPTION_NAME,
  )

  const sizeSectionSummarySegments = buildMiniPdpSizeSectionSummarySegments(
    props.product,
    props.selectedSiblingProductId,
    optionNameSelectedValueMap,
    sizeSectionEntries.map(e => ({
      key: e.key,
      optionName: e.optionName,
      displaySequence: e.displaySequence,
    })),
  )

  return (
    <div>
      {/* Color Option */}
      {combinedProductOptions
        .filter(option => option.key === ProductOptionName.Color)
        .map(option => (
          <div key={option.key} ref={colorRevealRef}>
            {option.node}
          </div>
        ))}
      {/* Size and merged Option */}
      {sizeSectionEntries.length > 0 && (
        <MiniPDPCollapsibleOptions
          summarySegments={sizeSectionSummarySegments}
          isExpandedSize={props.isExpandedSize}
          expandSizeMiniPDP={props.expandSizeMiniPDP}
        >
          {sizeSectionEntries.map(option => (
            <div key={String(option.key)}>{option.node}</div>
          ))}
        </MiniPDPCollapsibleOptions>
      )}
      {/* Other Options */}
      {combinedProductOptions
        .filter(
          option =>
            option.key !== ProductOptionName.Color &&
            option.key !== ProductOptionName.Size &&
            option.optionName !== MINI_PDP_MERGED_ADDON_OPTION_NAME,
        )
        .map(option => (
          <div key={option.key}>{option.node}</div>
        ))}
    </div>
  )
}

export default MiniPDPProductOptions
